import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import Navigation from "../../components/Navbar";
import Footer from '../../components/Footer';
import { 
  Users, 
  Wallet, 
  MapPin, 
  UserCheck, 
  LineChart, 
  Smartphone 
} from 'lucide-react';

// -----------------------------
// Service Data
// -----------------------------
const Services = [
  {
    id: 1,
    icon: <Users className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Peer-to-Peer Learning",
    description: "Connect students with tutors based on experience and expertise.",
    color: "border-blue-200"
  },
  {
    id: 2,
    icon: <Wallet className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Flexible Installments",
    description: "Pay for classes in parts .",
    color: "border-orange-200"
  },
  {
    id: 3,
    icon: <MapPin className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Physical Training",
    description: "Coming soon",
    color: "border-purple-200"
  },
  {
    id: 4,
    icon: <UserCheck className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Verified Tutors",
    description: "Tutors are vetted and rated by students for quality.",
    color: "border-green-200"
  },
  {
    id: 5,
    icon: <LineChart className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Trackable Progress",
    description: "Students can track their classes, payments, and tutors.",
    color: "border-cyan-200"
  },
  {
    id: 6,
    icon: <Smartphone className="text-[#1976D2] w-8 h-8 sm:w-10 sm:h-10" />,
    title: "Multi-Device Access",
    description: "Browse and enroll easily on mobile or desktop.",
    color: "border-red-200"
  },
];

// -----------------------------
// Service Card Component
// -----------------------------
const ServiceCard = ({ service, index }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 200, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 200, damping: 20 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / rect.width - 0.5;
    const yPct = mouseY / rect.height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateX: 20 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.21, 0.45, 0.32, 0.9] }}
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`relative bg-white rounded-[40px] p-10 border-2 ${service.color} shadow-[0_10px_30px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] transition-shadow duration-500 flex flex-col items-center text-center group cursor-pointer`}
    >
      <div style={{ transform: "translateZ(50px)" }} className="flex flex-col items-center">
        <div className="mb-8 p-5 bg-slate-50 rounded-[24px] group-hover:bg-[#1976D2]/10 group-hover:scale-110 transition-all duration-500 shadow-inner">
          {service.icon}
        </div>

        <h2 className="text-2xl font-bold mb-4 text-slate-800 tracking-tight">
          {service.title}
        </h2>

        <p className="text-slate-500 leading-relaxed text-balance">
          {service.description}
        </p>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100/50 rounded-[40px] opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.div>
  );
};

// -----------------------------
// Main Service Section
// -----------------------------
const ServiceSection = () => {
  return (
    <>
      <Navigation />

      <section className="w-full px-6 sm:px-12 lg:px-24 py-32 bg-[#FCFAF7] overflow-hidden perspective-[1500px]">
        <div className="max-w-4xl mx-auto text-center mb-24">
          <motion.h1 
            initial={{ opacity: 0, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1 }}
            className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-8 tracking-tighter"
          >
            Our <span className="text-[#1976D2] relative">
              Services
              <motion.svg 
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="absolute -bottom-3 left-0 w-full h-3" 
                viewBox="0 0 100 10" 
                fill="none"
              >
                <path d="M1 9C30 2 70 2 99 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </motion.svg>
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed"
          >
            Premium educational infrastructure designed for the next generation of African talent.
          </motion.p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {Services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </section>

      
    </>
  );
};

export default ServiceSection;